/**
 * Winnery Gateway API — Client Service
 * 
 * Handles communication with Winnery backend for:
 * - Currencies (list of available currencies with presets)
 * - Player Session (get player info by session_id)
 * - Wallet Balance (get player balance by session_id)
 * 
 * In demo mode (no backend URL), returns mock data.
 * 
 * @see INTEGRATION.md for backend connection guide
 */

import type {
  WinneryCurrency,
  WinneryPlayerSession,
  WinneryBalance,
  WinneryConfig,
} from '@/types/winnery';
import { DEFAULT_WINNERY_CONFIG } from '@/types/winnery';

// ==========================================
// Configuration
// ==========================================

let config: WinneryConfig = { ...DEFAULT_WINNERY_CONFIG };

/**
 * Update API configuration (e.g. set backend URL at runtime)
 */
export function configureWinneryApi(newConfig: Partial<WinneryConfig>) {
  config = { ...config, ...newConfig };
}

/**
 * Check if real API is configured
 */
export function isApiConfigured(): boolean {
  return !!config.baseUrl;
}

// ==========================================
// HTTP Helper
// ==========================================

async function apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
  if (!config.baseUrl) {
    throw new Error('Winnery API URL not configured');
  }

  const url = `${config.baseUrl}${endpoint}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeout);

  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options?.headers,
      },
      signal: controller.signal,
      ...options,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`API Error ${response.status}: ${errorBody || response.statusText}`);
    }

    return response.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ==========================================
// API Methods
// ==========================================

/**
 * Get list of available currencies with calculator presets.
 * Used for configuring the bet calculator.
 * 
 * GET /api/v1/currencies
 */
export async function getCurrencies(): Promise<WinneryCurrency[]> {
  if (!isApiConfigured()) {
    return getMockCurrencies();
  }
  
  try {
    const data = await apiRequest<WinneryCurrency[]>('/api/v1/currencies');
    return data;
  } catch (error) {
    console.warn('[WinneryAPI] getCurrencies failed, using mock:', error);
    return getMockCurrencies();
  }
}

/**
 * Get player session data by session_id.
 * session_id comes from URL query parameter (iframe integration).
 * 
 * GET /api/v1/players/session/:sessionId
 */
export async function getPlayerSession(sessionId: string): Promise<WinneryPlayerSession> {
  if (!isApiConfigured()) {
    return getMockPlayerSession(sessionId);
  }

  try {
    return await apiRequest<WinneryPlayerSession>(`/api/v1/players/session/${sessionId}`);
  } catch (error) {
    console.warn('[WinneryAPI] getPlayerSession failed, using mock:', error);
    return getMockPlayerSession(sessionId);
  }
}

/**
 * Get player balance by session_id.
 * Returns balance as human-readable string (already formatted by backend).
 * 
 * GET /api/v1/wallet/balance/:sessionId
 */
export async function getBalance(sessionId: string): Promise<WinneryBalance> {
  if (!isApiConfigured()) {
    return getMockBalance();
  }

  try {
    return await apiRequest<WinneryBalance>(`/api/v1/wallet/balance/${sessionId}`);
  } catch (error) {
    console.warn('[WinneryAPI] getBalance failed, using mock:', error);
    return getMockBalance();
  }
}

// ==========================================
// Mock Data (for development & demo)
// ==========================================

function getMockCurrencies(): WinneryCurrency[] {
  return [
    {
      id: 'usd',
      code: 'USD',
      name: 'US Dollar',
      default: true,
      decimals: 2,
      presets: [1, 2, 5, 10, 25, 50, 100],
    },
    {
      id: 'eur',
      code: 'EUR',
      name: 'Euro',
      default: false,
      decimals: 2,
      presets: [1, 2, 5, 10, 25, 50, 100],
    },
    {
      id: 'rub',
      code: 'RUB',
      name: 'Russian Ruble',
      default: false,
      decimals: 2,
      presets: [50, 100, 200, 500, 1000, 2000, 5000],
    },
  ];
}

function getMockPlayerSession(sessionId: string): WinneryPlayerSession {
  return {
    id: sessionId,
    player_id: `demo-player-${sessionId.slice(-6)}`,
    currency_code: 'USD',
    language: 'en',
    status: 'active',
  };
}

function getMockBalance(): WinneryBalance {
  return {
    amount: 500000,
    amount_human: '5 000.00',
    currency_code: 'USD',
  };
}

// ==========================================
// Utility: Extract session_id from URL
// ==========================================

/**
 * Extract session_id from URL query parameters.
 * The game is opened inside an iframe with ?session_id=xxx
 */
export function getSessionIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('session_id') || params.get('sessionId') || null;
}

// ==========================================
// Export all
// ==========================================

export const winneryApi = {
  getCurrencies,
  getPlayerSession,
  getBalance,
  getSessionIdFromUrl,
  configureWinneryApi,
  isApiConfigured,
};

export default winneryApi;







